import { Brick, BrickContext, registerBrick, ErrorFlow, Query, Cache } from 'olympe';
import Assessment from '../../../models/Assessment';
import Observation from '../../../models/Observation';
import Unit from '../../../models/Unit';
import System from '../../../models/System';
import Component from '../../../models/Component';
import Inspection from '../../../models/Inspection';
import AHPValue from '../../../models/AHPValue';
import Evaluation from '../../../models/Evaluation';
import Picture from '../../../models/Picture';
import InspectionEntity from '../../../models/InspectionEntity';
import Feedback from '../../../models/Feedback';
import InspectionState from '../../../models/InspectionState';
import Aspect from '../../../models/Aspect';
import InspectionResultMapping from '../../../models/InspectionResultMapping';

export default class QueryAllAssessmentData extends Brick {

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {Assessment} assessment
     * @param {string} cacheId
     * @param {boolean} isGuest
     * @param {boolean} withDocuments
     * @param {function()} forwardEvent
     * @param {function(ErrorFlow)} setError
     * @param {function(number)} setCurrentStatus
     */
    async update($, [assessment, cacheId, isGuest, withDocuments], [forwardEvent, setError, setCurrentStatus]) {

        if (!(assessment instanceof Assessment)) {
            setError(ErrorFlow.create(`assessment input must be an instance of an Assessment`, 1));
            return;
        }

        try {
            const queries = [];

            queries.push(Query.fromTag(assessment, Assessment));

            if (withDocuments) {
                queries.push(
                    assessment.follow(Assessment.introductionPicturesRel, true).andReturn()
                    .follow(Picture.fileRel).andReturn()
                    .back(2)
                    .follow(Assessment.conclusionPicturesRel, true).andReturn()
                    .follow(Picture.fileRel).andReturn()
                );
            }

            // Units
            queries.push(assessment.follow(Assessment.unitsRel));

            // Observation evaluations
            queries.push(
                assessment
                    .follow(Assessment.unitsRel)
                    .follow(Unit.observationsRel)
                    .follow(Observation.evalutationRel)
            );

            // Observation additional evals
            queries.push(
                assessment
                    .follow(Assessment.unitsRel)
                    .follow(Unit.observationsRel)    
                    .follow(Observation.additionalEvalsRel)
                );

            // Observation pictures
            queries.push(
                assessment
                    .follow(Assessment.unitsRel)
                    .follow(Unit.observationsRel)    
                    .follow(Observation.picturesRel).andReturn()
                    .follow(Picture.fileRel).andReturn()
            );    

            queries.push(
                Query.instancesOf(Aspect)
            );
            queries.push(
                Query.instancesOf(InspectionResultMapping)
            );

            // Feedbacks
            const feedbacks = assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.feedbacksRel).andReturn()
                .follow(Feedback.targetRel, true);
            queries.push(withDocuments
                ? feedbacks.back().follow(Feedback.documentsRel, true).andReturn()
                : feedbacks
            );

            // States
            queries.push(
                assessment
                    .follow(Assessment.unitsRel)
                    .follow(Unit.statesRel).andReturn()
                    .follow(InspectionState.dependenciesRel, true).andReturn()
            );

            queries.push(...(isGuest ? this.getGuestSpecificQueries(assessment, withDocuments) : this.getExpertSpecificQueries(assessment, withDocuments)));

            const assessmentName = assessment.get(Assessment.titleProp);
            let processingStarted = false;
            const sub = Cache.getProcessingStatus().subscribe((status) => {
                let message = '';
                if (status.processing) {
                    processingStarted = true;
                    message = `Download ${assessmentName} data for offline use`;
                    if ('queries' in status) {
                        const {total, processed} = status.queries;
                        message += `\nData: ${processed} / ${total}`
                    } else {
                        message += `\nData: finished`
                    }

                    if ('files' in status) {
                        const {total, processed} = status.files;
                        message += `\nDownload pictures: ${processed} / ${total}`
                    }
                } else if (processingStarted) {
                    message = `Assessment ${assessmentName} is ready`;
                    setTimeout(() => sub?.unsubscribe(), 1000);
                }
                setCurrentStatus(message);
            });
            await Promise.all(queries.map((query) => query.execute({cacheId})));

            forwardEvent();
        } catch (error) {
            setError(ErrorFlow.create(`Error when querying all data for assessment "${assessment.getTag()}": ${error}`, 2));
        }
    }

    /**
     * @private
     * @param {Assessment} assessment
     * @param {boolean} withDocuments
     * @return {Query[]}
     */
    getExpertSpecificQueries(assessment, withDocuments) {
        const queries = [];

        // Observation with target
        queries.push(
            assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.observationsRel).andReturn()
                .follow(Observation.targetRel)
        );

        // Systems
        const systems = assessment
            .follow(Assessment.unitsRel)
            .follow(Unit.systemsRel).andReturn()
            .follow(InspectionEntity.picturesRel, true).andReturn()
            .follow(Picture.fileRel).andReturn();

            queries.push(systems);

            // Documents of the system (split to avoid losing the info about system if no document)
            withDocuments && queries.push(assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.systemsRel)
                .follow(InspectionEntity.documentsRel).andReturn()
            );


        // Components
        queries.push(
            assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.systemsRel)
                .follow(System.allComponentsRel)
        );

        const components = assessment
            .follow(Assessment.unitsRel)
            .follow(Unit.systemsRel)
            .follow(System.allComponentsRel)
            .follow(InspectionEntity.picturesRel, true).andReturn()
            .follow(Picture.fileRel).andReturn()
        queries.push(withDocuments
            ? components.back(2).follow(InspectionEntity.documentsRel, true).andReturn()
            : components
        );

        // Inspections
        queries.push(
            assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.systemsRel)
                .follow(System.allComponentsRel)
                .follow(Component.inspectionsRel).andReturn()
                .follow(InspectionEntity.presumedStatesRel, true).andReturn()
        );
        const inspections = assessment
            .follow(Assessment.unitsRel)
            .follow(Unit.systemsRel)
            .follow(System.allComponentsRel)
            .follow(Component.inspectionsRel)
            .follow(InspectionEntity.picturesRel, true).andReturn()
            .follow(Picture.fileRel).andReturn()
        queries.push(withDocuments
            ? inspections.back(2).follow(InspectionEntity.documentsRel, true).andReturn()
            : inspections
        );

        queries.push(
            assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.systemsRel).andReturn()
                .follow(System.allComponentsRel)
                .follow(Component.subComponentsRel)
        );

        // Evaluations
          queries.push(
            assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.systemsRel)
                .follow(System.allComponentsRel)
                .follow(Component.inspectionsRel)
                .follow(Inspection.evaluationsListRel)
        );
        queries.push(
            assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.systemsRel)
                .follow(System.allComponentsRel)
                .follow(Component.inspectionsRel)
                .follow(Inspection.evaluationsListRel).andReturn()
                .follow(Evaluation.picturesRel).andReturn()
                .follow(Picture.fileRel).andReturn()
        );

        // Relations of system tree
        queries.push(
            assessment
                .follow(Assessment.unitsRel).andReturn()
                .follow(Unit.systemsRel)
                .follow(System.componentsRel)
        );
        queries.push(
            assessment
                .follow(Assessment.unitsRel).andReturn()
                .follow(Unit.systemsRel)
                .follow(System.allComponentsRel)
                .follow(Component.subComponentsRel)
        );

        // AHP values
        queries.push(
            assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.systemsRel)
                .follow(System.allComponentsRel)
                .follow(AHPValue.itemRel.getInverse()).andReturn()
                .follow(AHPValue.aspectRel)
        );
        queries.push(
            assessment
                .follow(Assessment.unitsRel)
                .follow(Unit.systemsRel)
                .follow(System.allComponentsRel)
                .follow(Component.inspectionsRel)
                .follow(AHPValue.itemRel.getInverse()).andReturn()
                .follow(AHPValue.aspectRel)
        );

        return queries;
    }

    /**
     * @private
     * @param {Assessment} assessment
     * @param {boolean} withDocuments
     * @return {Query[]}
     */
    getGuestSpecificQueries(assessment, withDocuments) {
        const queries = [];

        // Systems - Components and Inspections
        queries.push(
            assessment.follow(Assessment.unitsRel)
                .follow(Unit.observationsRel).andReturn()
                .follow(Observation.targetRel).andReturn() // Inspection Entity
        );

        // Pictures and documents of Entities
        const pictures = assessment.follow(Assessment.unitsRel)
            .follow(Unit.observationsRel)
            .follow(Observation.targetRel) // Inspection Entity
            .follow(InspectionEntity.picturesRel, true).andReturn()
            .follow(Picture.fileRel).andReturn()
        queries.push(withDocuments
            ? pictures.back(2).follow(InspectionEntity.documentsRel, true).andReturn()
            : pictures
        );

        // System tree structure (relations only)
        queries.push(
            assessment.follow(Assessment.unitsRel).andReturn()
                .follow(Unit.systemsRel)
                .follow(System.allComponentsRel, true)
                .follow(Component.inspectionsRel, true)
                .follow(InspectionEntity.presumedStatesRel, true).andReturn()
        );

         // Relations of system tree
        queries.push(
            assessment
                .follow(Assessment.unitsRel).andReturn()
                .follow(Unit.systemsRel)
                .follow(System.componentsRel)
        );

        queries.push(
            assessment.follow(Assessment.unitsRel).andReturn()
                .follow(Unit.systemsRel)
                .follow(System.allComponentsRel, true)
                .follow(Component.subComponentsRel, true)
                .back()
                .follow(System.componentsRel.getInverse(), true)
        );

        // Evaluations
        queries.push(
            assessment.follow(Assessment.unitsRel)
                .follow(Unit.observationsRel)
                .follow(Observation.targetRel) // Inspection Entity
                .follow(Inspection.evaluationsListRel)
        );
        queries.push(
            assessment.follow(Assessment.unitsRel)
                .follow(Unit.observationsRel)
                .follow(Observation.targetRel) // Inspection Entity
                .follow(Inspection.evaluationsListRel)
                .follow(Evaluation.picturesRel).andReturn()
                .follow(Picture.fileRel).andReturn()
        );

        // AHP values
        queries.push(
            assessment.follow(Assessment.unitsRel)
                .follow(Unit.observationsRel)
                .follow(Observation.targetRel) // Inspection Entity
                .follow(AHPValue.itemRel.getInverse()).andReturn()
                .follow(AHPValue.aspectRel)
        );

        return queries;
    }
}

registerBrick('018a640d0087ac97d1e3', QueryAllAssessmentData);
